import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";
import React, { Suspense, lazy } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Loader } from "@/components/ui/loader";
import Login from "./pages/Login";
import Landing from "./pages/landing";
import AllCourses from './pages/coursestore';
import { Toaster } from "react-hot-toast";
import useAuthStore from "./stores/authStore";
import api from "@/pages/services/api";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getPageTransitionConfig } from "@/animations/pageTransitions";
import { isMobileOrReducedMotion } from "@/utils/device";

// Layout
import Layout from "./components/Layout";

// Onboarding
const OnboardingForm = lazy(() => import("./pages/Onboardingform"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const Users = lazy(() => import("./pages/admin/Users"));
const Analysis = lazy(() => import("./pages/admin/Analysis"));
const CalendarPage = lazy(() => import("./pages/admin/Calendar"));
const Availability = lazy(() => import("./pages/admin/Availability"));
const Chats = lazy(() => import("./pages/admin/Chats"));
const AttendanceDashboard = lazy(() => import("./pages/attendance/AttendanceDashboard"));
const Placements = lazy(() => import("./pages/admin/Placements"));
const RecordedCourseDetailPage = lazy(() => import("./pages/courses/RecordedCourseDetailPage"));
const RecordedCoursesCompletion = lazy(() => import("./pages/courses/RecordedCoursesCompletion"));
const JoinCoursePage = lazy(() => import("./pages/JoinCoursePage"));
const CourseInvitePage = lazy(() => import("./pages/CourseInvitePage"));
// Unified public landing page. Resolves /c/:identifier where :identifier
// is either a course custom slug OR a session shareable_link_uuid.
const PublicLandingPage = lazy(() => import("./pages/PublicCoursePage"));

// Instructor pages
const InstructorGetStarted = lazy(() => import("./pages/instructor/InstructorGetStarted"));
const Assignment = lazy(() => import("./pages/instructor/Assignment"));
const InstructorHome = lazy(() => import("./pages/instructor/InstructorHome"));
const InstructorPayouts = lazy(() => import("./pages/instructor/InstructorPayouts"));

// Learner pages
const LearnerGetStarted = lazy(() => import("./pages/learner/LearnerGetStarted"));
const CareerProfile = lazy(() => import("./pages/learner/CareerProfile"));
const LearnerCalendar = lazy(() => import("./pages/learner/LearnerCalendar"));
const LearnersCourse = lazy(() => import("./pages/courses/LearnersCourse"));
const LearnerCoursePage = lazy(() => import("./pages/courses/LearnerCoursePage"));
const CoursesList = lazy(() => import("./pages/learner/CoursesList"));
const LearnerFeesPage = lazy(() => import("./pages/learner/Fees"));
const LearnerAttendance = lazy(() => import("./pages/learner/LearnerAttendance"));

// Resume (earned skills + ATS)
const ResumePage = lazy(() => import("./pages/resume/ResumePage"));

// Shared
const Setting = lazy(() => import("./components/Setting"));
const Notifications = lazy(() => import("./components/Notification"));

// Courses
const Courses = lazy(() => import("./pages/courses/Courses"));
const GroupCourses = lazy(() => import("./pages/courses/GroupCourses"));
const CourseDetailPage = lazy(() => import("./pages/courses/CourseDetailPage"));
const CoursePreview = lazy(() => import("./pages/courses/CoursePreview"));
const RecordedCourses = lazy(() => import("./pages/courses/RecordedCourses"));
const LiveCourses = lazy(() => import("./pages/courses/LiveCourses"));
const CreateQuestionPaper = lazy(() => import("./pages/courses/components/CreateQuestionPaper"));
const TestSettings = lazy(() => import("./pages/courses/components/TestSetting"));

// Meeting handoff (redirects to Zoom URL/app)
const MeetingRoom = lazy(() => import("./pages/meeting/MeetingRoom"));

const ResetPin = lazy(() => import("./components/ResetPin"));
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmail"));
const ForgotPin = lazy(() => import("./pages/auth/ForgotPin"));
const VerifyResetOtp = lazy(() => import("./pages/auth/VerifyResetOtp"));
const SetNewPin = lazy(() => import("./pages/auth/SetNewPin"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5 * 60 * 1000, gcTime: 10 * 60 * 1000, retry: 1, refetchOnWindowFocus: false },
  },
});

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// --- ROUTE HANDLERS ---

// Internal-only redirect targets are accepted post-login. Rejects anything
// that looks like an open-redirect (protocol-relative, absolute URL, javascript:).
const isSafeInternalPath = (p) =>
  typeof p === "string" &&
  p.startsWith("/") &&
  !p.startsWith("//") &&
  !/^\/+\w+:/.test(p);

const isUuidIdentifier = (identifier) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(identifier || "")
  );

const getPublicSessionIdentifierFromPath = (path) => {
  const match = String(path || "").match(
    /^\/c\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i
  );
  return match?.[1] || "";
};

// Fetch the canonical meet.google.com URL via axios (which already
// resolves VITE_HOST correctly in every working LMS deployment),
// then top-level-navigate the browser directly to Meet. This avoids
// the previous failure mode where `window.location.replace` to
// `${origin}/api/live-courses/public-join/...` fell through to the
// SPA's catch-all `<Route path="*">` whenever the deployment didn't
// proxy `/api/*` for top-level navigations — bouncing unauthenticated
// users to `/`. Caller renders <Loading /> while this is in flight;
// success ends in a hard nav so this function never resolves on the
// happy path.
const redirectToPublicJoin = async (identifier) => {
  if (!identifier) return false;
  try {
    const res = await api.get(
      `/live-courses/public-join/${encodeURIComponent(identifier)}`,
      { skipAuth: true }
    );
    const data = res?.data || {};
    if (data.joinUrl) {
      window.location.replace(data.joinUrl);
      return true;
    }
    if (data.waitingForHost || data.requiresJoinerDetails || data.sessionStartedAt == null) {
      return false;
    }
    return false;
  } catch (err) {
    // 409 + waitingForHost is the server holding the URL back until the host
    // starts — falling through renders the branded waiting screen.
    const body = err?.response?.data || {};
    if (body.waitingForHost || body.requiresJoinerDetails || body.sessionStartedAt == null) {
      return false;
    }
    console.error("[public-join] fetch failed:", err);
    return false;
  }
};

const LoginRouteHandler = () => {
  const { isAuthenticated, user, accessToken, isGlobalOnboardingRequired } = useAuthStore();
  const storedUser = readStoredUser();
  const effectiveUser = user || storedUser;
  const token = accessToken || localStorage.getItem("token");
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEffectivelyAuthenticated = Boolean(isAuthenticated && token && effectiveUser?.role);
  const isNewSignup = sessionStorage.getItem("isNewSignUp") === "true";

  // Course invite: if the user arrived via /course-invite/<token>, the page
  // stashed the token in sessionStorage before bouncing them to /login. Route
  // them back to /course-invite/<token> once authenticated — CourseInvitePage
  // is the single join handler (it owns the free/paid/waitlist outcomes and the
  // Razorpay checkout), so we no longer fire any join from here. The stash is a
  // fallback for the login path (the ?redirect= param normally handles it) and
  // the carry-through for new signups, who go via /onboarding first (which then
  // routes to the invite page). We do NOT clear it here — that would wipe it
  // before onboarding consumes it; CourseInvitePage clears it once landed.
  const pendingInviteToken = (() => {
    try { return sessionStorage.getItem("pendingCourseInviteToken"); } catch { return null; }
  })();
  const pendingInviteRedirect = pendingInviteToken ? `/course-invite/${pendingInviteToken}` : null;

  // Post-login redirect: prefer in-memory state (set by ProtectedRoute on
  // the same SPA navigation) and fall back to ?next= so a user who opened
  // the protected link in a fresh tab still lands back where they wanted.
  const stateRedirect = location.state?.postLoginRedirect;
  const queryRedirect = searchParams.get("next") || searchParams.get("redirect");
  const publicSessionIdentifier = getPublicSessionIdentifierFromPath(queryRedirect);

  if (publicSessionIdentifier) {
    redirectToPublicJoin(publicSessionIdentifier);
    return <Loading />;
  }

  const requestedRedirect =
    isSafeInternalPath(stateRedirect) ? stateRedirect :
    isSafeInternalPath(queryRedirect) ? queryRedirect :
    isSafeInternalPath(pendingInviteRedirect) ? pendingInviteRedirect :
    null;

  console.log("[VISIONRISE DEBUG - ROUTER] Reached /login route.");
  console.log("[VISIONRISE DEBUG - ROUTER] Current State -> Authenticated:", isEffectivelyAuthenticated, "| isNewSignup Flag:", isNewSignup);

  if (!isEffectivelyAuthenticated) {
    console.log("[VISIONRISE DEBUG - ROUTER] User is NOT logged in. Rendering Login component.");
    return <Login />;
  }

  if (isNewSignup && isGlobalOnboardingRequired) {
    console.log("[VISIONRISE DEBUG - ROUTER] User IS logged in AND just signed up. Redirecting to /onboarding");
    return <Navigate to="/onboarding" replace />;
  }

  if (requestedRedirect) {
    console.log(`[VISIONRISE DEBUG - ROUTER] Using post-login redirect: ${requestedRedirect}`);
    return <Navigate to={requestedRedirect} replace />;
  }

  console.log(`[VISIONRISE DEBUG - ROUTER] User IS logged in (Normal flow). Redirecting to /${effectiveUser?.role || 'learner'}`);
  return <Navigate to={`/${effectiveUser?.role || 'learner'}`} replace />;
};

const ProtectedRoute = ({ role, children }) => {
  const { isAuthenticated, user, accessToken, isGlobalOnboardingRequired } = useAuthStore();
  const storedUser = readStoredUser();
  const effectiveUser = user || storedUser;
  const token = accessToken || localStorage.getItem("token");
  const location = useLocation();
  const isEffectivelyAuthenticated = Boolean(isAuthenticated && token && effectiveUser?.role);
  const publicSessionIdentifier = getPublicSessionIdentifierFromPath(location.pathname);

  if (publicSessionIdentifier) {
    redirectToPublicJoin(publicSessionIdentifier);
    return <Loading />;
  }

  if (!isEffectivelyAuthenticated) {
    // Preserve the path-the-user-wanted as both navigation state (works
    // for in-SPA bounces) and ?next= query param (works if the user opened
    // a protected link in a new tab and there is no prior state).
    const intended = location.pathname + location.search;
    console.log("[VISIONRISE DEBUG - PROTECTED] Access Denied. Redirecting to /login with next=", intended);
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(intended)}`}
        replace
        state={{ postLoginRedirect: intended }}
      />
    );
  }

  if (role && effectiveUser?.role !== role) {
    console.log(`[VISIONRISE DEBUG - PROTECTED] Role Mismatch. Required: ${role}, User has: ${effectiveUser.role}. Redirecting.`);
    return <Navigate to={`/${effectiveUser?.role || 'learner'}`} replace />;
  }

  // Enforce Onboarding for Learners — only when the admin switch keeps it mandatory
  if (effectiveUser?.role === "learner" && isGlobalOnboardingRequired && !effectiveUser?.isOnboarded && location.pathname !== "/onboarding") {
    console.log("[VISIONRISE DEBUG - PROTECTED] Onboarding Compulsory for Learners. Redirecting...");
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Tiny redirect wrapper used for legacy public links. /course/:slug and
// /onlineClass/:sessionId are kept as routes so shared old URLs don't 404,
// and they both forward to the canonical /c/:identifier path. `replace`
// keeps the back button useful — users don't bounce through the old URL.
const RedirectToCanonical = () => {
  const { identifier } = useParams();
  return <Navigate to={`/c/${identifier || ""}`} replace />;
};

// UUID links are session links and must never touch auth-gated UI. Keep this
// tiny route wrapper in App.jsx so /c/:uuid leaves the SPA before lazy-loading
// PublicCoursePage, which still supports public course slugs and their
// sign-in-to-request-access flow. If the redirect fails (bad UUID, network
// error, backend unreachable), we fall through to PublicLandingPage so the
// user sees a real "not found" message instead of being stuck on the loader.
const PublicCourseRoute = () => {
  const { identifier } = useParams();
  const [redirectFailed, setRedirectFailed] = React.useState(false);

  React.useEffect(() => {
    if (!isUuidIdentifier(identifier)) return;
    let cancelled = false;
    redirectToPublicJoin(identifier).then((ok) => {
      if (!ok && !cancelled) setRedirectFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [identifier]);

  if (isUuidIdentifier(identifier) && !redirectFailed) {
    return <Loading />;
  }

  return <PublicLandingPage />;
};

const Loading = () => (
  <div className="flex h-screen w-full items-center justify-center bg-background">
    <Loader size="md" />
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  const { variants, transition } = getPageTransitionConfig(isMobileOrReducedMotion());

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        <Suspense fallback={<Loading />}>
          <Routes location={location}>
            {/* PUBLIC ROUTES */}
            <Route path="/" element={<Landing />} />
            <Route path="/coursestore" element={<AllCourses />} />
            <Route path="/join-course" element={<JoinCoursePage />} />
            <Route path="/course-invite/:token" element={<CourseInvitePage />} />

            {/* Canonical public landing page. The :identifier may be a
                course custom-slug ("test-course") OR a session UUID
                ("a1b2c3d4-..."). Session links are publicly joinable. */}
            <Route
              path="/c/:identifier"
              element={<PublicCourseRoute />}
            />

            {/* Legacy redirects — preserve old shared links by forwarding
                them to the canonical /c/:identifier path. The two old
                surfaces (/course/:slug for slugs, /onlineClass/:uuid for
                shareable UUIDs) are now both subsumed by /c/. */}
            <Route path="/course/:identifier" element={<RedirectToCanonical />} />
            <Route path="/onlineClass/:identifier" element={<RedirectToCanonical />} />

            {/* LOGIN & SIGNUP */}
            <Route path="/login" element={<LoginRouteHandler />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-pin" element={<ForgotPin />} />
            <Route path="/verify-reset-otp" element={<VerifyResetOtp />} />
            <Route path="/set-new-pin" element={<SetNewPin />} />
            <Route path="/reset-pin" element={<ResetPin />} />

            {/* ONBOARDING FORM */}
            <Route path="/onboarding/:userId?" element={<ProtectedRoute><OnboardingForm /></ProtectedRoute>} />

            {/* LIVE MEETING HANDOFF. Reached internally after the user clicks
                "Join" on /c/:identifier, then redirects to Zoom directly. */}
            <Route path="/meeting/:sessionId" element={<ProtectedRoute><MeetingRoom /></ProtectedRoute>} />

            {/* ADMIN DASHBOARD */}
            <Route path="/admin" element={<ProtectedRoute role="admin"><Layout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<Users />} />
              <Route path="analysis" element={<Analysis />} />
              <Route path="setting" element={<Setting />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="availability" element={<Availability />} />
              <Route path="chats" element={<Chats />} />
              <Route path="attendance" element={<AttendanceDashboard />} />
              <Route path="placements" element={<Placements />} />

              {/* Resume tab — ADMIN-ONLY during testing (intentionally hidden
                  from learners until the feature is verified). Move back to the
                  learner tree to launch it. */}
              <Route path="resume" element={<ResumePage />} />

              <Route path="courses" element={<Courses />}>
                {/* Default landing for /<role>/courses — without this index
                    the wrapper's <Outlet /> renders empty and the page goes
                    blank, which is what was happening on the instructor
                    side. Redirects to /group as the canonical default. */}
                <Route index element={<Navigate to="group" replace />} />
                <Route path="group">
                  <Route index element={<GroupCourses />} />
                  <Route path=":courseId" element={<CourseDetailPage />} />
                  <Route path=":courseId/tests/:testId/questions" element={<CreateQuestionPaper />} />
                  <Route path=":courseId/tests/:testId/settings" element={<TestSettings />} />
                </Route>
                <Route path="preview" element={<CoursePreview />} />
                <Route path="recorded">
                  <Route index element={<RecordedCourses />} />
                  <Route path=":courseId" element={<RecordedCourseDetailPage />} />
                  <Route path=":courseId/overview" element={<RecordedCoursesCompletion />} />
                  <Route path=":courseId/tests/:testId/questions" element={<CreateQuestionPaper />} />
                  <Route path=":courseId/tests/:testId/settings" element={<TestSettings />} />
                </Route>
                <Route path="live">
                  <Route index element={<LiveCourses />} />
                  <Route path=":courseId" element={<CourseDetailPage />} />
                  <Route path=":courseId/tests/:testId/questions" element={<CreateQuestionPaper />} />
                  <Route path=":courseId/tests/:testId/settings" element={<TestSettings />} />
                </Route>
              </Route>
            </Route>

            {/* INSTRUCTOR DASHBOARD */}
            <Route path="/instructor" element={<ProtectedRoute role="instructor"><Outlet /></ProtectedRoute>}>
              <Route element={<Layout />}>
                <Route index element={<InstructorHome />} />
                <Route path="home" element={<InstructorHome />} />
                <Route path="setting" element={<Setting />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="availability" element={<Availability />} />
                {/* Attendance Portal removed for instructors — admin-only
                    feature. Backend mutation routes are already isAdmin
                    gated; removing the read view too eliminates the
                    confusion of seeing the navigation entry. */}
                <Route path="payouts" element={<InstructorPayouts />} />
                <Route path="assignment" element={<Assignment />} />
                <Route path="courses" element={<Courses />}>
                  <Route index element={<Navigate to="group" replace />} />
                  <Route path="group">
                    <Route index element={<GroupCourses />} />
                    <Route path=":courseId" element={<CourseDetailPage />} />
                    <Route path=":courseId/tests/:testId/questions" element={<CreateQuestionPaper />} />
                    <Route path=":courseId/tests/:testId/settings" element={<TestSettings />} />
                  </Route>
                  <Route path="recorded">
                    <Route index element={<RecordedCourses />} />
                    <Route path=":courseId" element={<RecordedCourseDetailPage />} />
                    <Route path=":courseId/overview" element={<RecordedCoursesCompletion />} />
                    <Route path=":courseId/tests/:testId/questions" element={<CreateQuestionPaper />} />
                    <Route path=":courseId/tests/:testId/settings" element={<TestSettings />} />
                  </Route>
                  {/* Live (1:1) courses removed from instructor surface — they
                      remain admin-only; instructors only manage Group + Recorded. */}
                  <Route path="live" element={<Navigate to="../group" replace />} />
                </Route>
              </Route>
            </Route>

            {/* LEARNER DASHBOARD */}
            <Route path="/learner" element={<ProtectedRoute role="learner"><Layout /></ProtectedRoute>}>
              <Route index element={<LearnerGetStarted />} />
              <Route path="calendar" element={<LearnerCalendar />} />
              <Route path="setting" element={<Setting />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="attendance" element={<LearnerAttendance />} />
              <Route path="career" element={<CareerProfile />} />
              <Route path="courses" element={<LearnersCourse />} />
              <Route path="courses/all" element={<CoursesList />} />
              <Route path="fees" element={<LearnerFeesPage />} />
              <Route path="course/:courseId" element={<LearnerCoursePage />} />
              <Route path="chats" element={<Chats />} />
            </Route>

            {/* FALLBACK */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Toaster position="bottom-center" toastOptions={{ duration: 4000 }} />
        <AnimatedRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
